import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { styled, useThemeProps } from '@mui/material/styles';
import { motion } from 'framer-motion';
import '../../theme/customTheme.js';
import '../../theme/defaultTheme.js';
import { resolveThemeColor, createResponsiveStyles } from '../../theme/util.js';
import '../../theme/componentNames.js';
import '../../utils/stringUtils.js';
import '@mui/material';
import 'lodash';
import 'lodash/omitBy';
import 'lodash/isNil';
import React__default from 'react';

const ButtonBase = styled(motion.button)(() => ({
    fontFamily: 'inherit',
    lineHeight: 'inherit',
}));
const convertHeightToResponsiveStyles = (height) => {
    if (height == null) {
        return;
    }
    const heightValue = typeof height === 'number' || typeof height === 'string'
        ? {
            xs: height,
            sm: height,
            md: height,
            lg: height,
            xl: height,
        }
        : height;
    return Object.entries(heightValue).reduce((acc, [key, value]) => {
        acc[key] = {
            height: value,
        };
        return acc;
    }, {});
};
const SolidButton = styled(ButtonBase, {
    name: 'BCSolidButton',
    slot: 'Root',
})(({ theme, ownerState }) => {
    var _a, _b, _c;
    const { color = 'primary', buttonName, disableMaxWidth, increaseSpecificity, height, fullWidth, disableResponsiveStyles, } = ownerState;
    const themeColor = resolveThemeColor(theme, color);
    const cssStyleOverrides = buttonName && ((_a = theme.componentCSSOverrides) === null || _a === void 0 ? void 0 : _a[buttonName]);
    const heightStyles = convertHeightToResponsiveStyles(height);
    const defaultStyles = Object.assign(Object.assign({ backgroundColor: themeColor.main, color: themeColor.contrastText }, (fullWidth && { width: '100%' })), (disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)));
    const styles = Object.assign(Object.assign({ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none', font: theme.vars.font.button, fontWeight: (_b = theme.typography.button) === null || _b === void 0 ? void 0 : _b.fontWeight }, defaultStyles), { ':hover': Object.assign({}, defaultStyles), transition: 'transform 0.3s', '&:active': {
            '&:not([disabled])': {
                transform: 'scale(0.95)',
            },
        }, 'not(:focus-visible)': {
            outline: 'none',
        }, padding: '8.5px 17px', ':disabled': {
            cursor: 'not-allowed',
            opacity: 1,
        }, [theme.breakpoints.down('sm')]: Object.assign({ font: theme.vars.font.smButton, fontWeight: (_c = theme.typography.smButton) === null || _c === void 0 ? void 0 : _c.fontWeight }, (!disableMaxWidth && { maxWidth: 333 })), '.left_icon': {
            marginRight: 6,
        }, '.button_img': {
            marginRight: 6,
            maxWidth: 18,
            maxHeight: 18,
        } });
    if (increaseSpecificity) {
        return {
            '&&': Object.assign({}, styles),
        };
    }
    return styles;
});
const OutlineButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => !['disableMaxWidth', 'sx', 'increaseSpecificity'].includes(prop),
    name: 'BCOutlineButton',
    slot: 'Root',
})(({ theme, ownerState }) => {
    var _a, _b, _c;
    const { color = 'primary', buttonName, disableMaxWidth, increaseSpecificity, height, fullWidth, disableResponsiveStyles, } = ownerState;
    const themeColor = resolveThemeColor(theme, color);
    const cssStyleOverrides = buttonName && ((_a = theme.componentCSSOverrides) === null || _a === void 0 ? void 0 : _a[buttonName]);
    const heightStyles = convertHeightToResponsiveStyles(height);
    const defaultStyles = Object.assign(Object.assign({ color: themeColor.main, borderColor: themeColor.main, backgroundColor: 'transparent' }, (fullWidth && { width: '100%' })), (disableResponsiveStyles ? {} : createResponsiveStyles(theme, heightStyles, cssStyleOverrides)));
    const styles = Object.assign(Object.assign({ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', font: theme.vars.font.button, fontWeight: (_b = theme.typography.button) === null || _b === void 0 ? void 0 : _b.fontWeight }, defaultStyles), { ':hover': Object.assign({}, defaultStyles), transition: 'transform 0.3s', '&:active': {
            '&:not([disabled])': {
                transform: 'scale(0.95)',
            },
        }, 'not(:focus-visible)': {
            outline: 'none',
        }, padding: '7px 17px', ':disabled': {
            opacity: 1,
            color: theme.vars.palette.action.disabled,
            cursor: 'not-allowed',
            borderColor: theme.vars.palette.action.disabled,
        }, [theme.breakpoints.down('sm')]: Object.assign({ font: theme.vars.font.smButton, fontWeight: (_c = theme.typography.smButton) === null || _c === void 0 ? void 0 : _c.fontWeight }, (!disableMaxWidth && { maxWidth: 333 })), '.button_img': {
            marginRight: 6,
            maxWidth: 18,
            maxHeight: 18,
        } });
    if (increaseSpecificity) {
        return {
            '&&': Object.assign({}, styles),
        };
    }
    return styles;
});
const TextButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => !['sx', 'increaseSpecificity'].includes(prop),
    name: 'BCTextButton',
    slot: 'Root',
})(({ theme, ownerState }) => {
    var _a, _b, _c;
    const { color = 'primary', buttonName, increaseSpecificity, fullWidth, disableResponsiveStyles } = ownerState;
    const resolvedColor = color === 'default' ? theme.vars.palette.text.primary : (_a = resolveThemeColor(theme, color)) === null || _a === void 0 ? void 0 : _a.main;
    const cssStyleOverrides = buttonName && ((_b = theme.componentCSSOverrides) === null || _b === void 0 ? void 0 : _b[buttonName]);
    const defaultStyles = Object.assign(Object.assign({ color: resolvedColor, backgroundColor: 'transparent' }, (fullWidth && { width: '100%' })), (disableResponsiveStyles ? {} : createResponsiveStyles(theme, cssStyleOverrides)));
    const styles = Object.assign(Object.assign({ font: theme.vars.font.button, fontWeight: (_c = theme.typography.button) === null || _c === void 0 ? void 0 : _c.fontWeight }, defaultStyles), { textTransform: 'unset', width: 'auto', height: 'auto', minWidth: 'fit-content', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 'unset', 'not(:focus-visible)': {
            outline: 'none',
        }, border: 'none', padding: 0, margin: 0, fontSize: 'inherit', ':disabled': Object.assign(Object.assign({}, defaultStyles), { cursor: 'not-allowed', opacity: 1, color: theme.vars.palette.action.disabled }), '.left_icon': {
            marginRight: 6,
        }, '.button_img': {
            marginRight: 6,
            maxWidth: 18,
            maxHeight: 18,
        } });
    if (increaseSpecificity) {
        return {
            '&&': Object.assign({}, styles),
        };
    }
    return styles;
});
const IconButton = styled(TextButton, {
    shouldForwardProp: (prop) => !['sx', 'increaseSpecificity'].includes(prop),
    name: 'BCIconButton',
    slot: 'Root',
    skipVariantsResolver: true,
})(() => {
    // const { height, increaseSpecificity } = ownerState;
    // const squareSizeStyles = createResponsiveStyles(theme, convertSquareSizeToResponsiveStyles(height));
    // const styles = {
    //   ...squareSizeStyles,
    // };
    // if (increaseSpecificity) {
    //   return {
    //     '&&': {
    //       ...styles,
    //     },
    //   };
    // }
    // return styles;
    return {};
});
const Button = React__default.forwardRef((_a, ref) => {
    var { variant = 'solid' } = _a, inProps = __rest(_a, ["variant"]);
    const ButtonComponentName = {
        solid: 'BCSolidButton',
        outline: 'BCOutlineButton',
        text: 'BCTextButton',
        icon: 'BCIconButton',
    }[variant];
    const props = useThemeProps({
        props: inProps,
        name: ButtonComponentName,
    });
    const { disableMaxWidth, color, buttonName, increaseSpecificity, height } = props, rest = __rest(props, ["disableMaxWidth", "color", "buttonName", "increaseSpecificity", "height"]);
    // The ownerState is a special name that will not spread to the DOM via the styled API
    // https://v6.mui.com/material-ui/customization/creating-themed-components/#3-style-the-slot-with-ownerstate
    const ownerState = Object.assign(Object.assign({}, props), { variant,
        disableMaxWidth,
        color,
        buttonName,
        increaseSpecificity,
        height });
    switch (variant) {
        case 'solid': {
            if (props.disabled) {
                return (React__default.createElement(OutlineButton, Object.assign({ ref: ref, type: "button", ownerState: ownerState, "data-theme-color": color, "data-theme-variant": "outline-button", "data-theme-component-name": buttonName }, rest)));
            }
            // Submit type allows form submission on enter key press
            return (React__default.createElement(SolidButton, Object.assign({ ref: ref, type: "submit", ownerState: ownerState, "data-theme-color": color, "data-theme-variant": "solid-button", "data-theme-component-name": buttonName }, rest)));
        }
        case 'outline':
            return (React__default.createElement(OutlineButton, Object.assign({ ref: ref, type: "button", ownerState: ownerState, "data-theme-color": color, "data-theme-variant": "outline-button", "data-theme-component-name": buttonName }, rest)));
        case 'text':
            return (React__default.createElement(TextButton, Object.assign({ ref: ref, type: "button", ownerState: ownerState, "data-theme-color": color, "data-theme-variant": "text-button", "data-theme-component-name": buttonName }, rest)));
        case 'icon':
            return (React__default.createElement(IconButton, Object.assign({ ref: ref, type: "button", ownerState: ownerState, "data-theme-color": color, "data-theme-variant": "icon-button", "data-theme-component-name": buttonName, disableResponsiveStyles: true }, rest)));
        default:
            throw new Error(`Invalid variant: ${variant}. Expected 'solid', 'outline', or 'text'.`);
    }
});
Button.displayName = 'Button';

export { Button, Button as default };
//# sourceMappingURL=Button.js.map
